import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode } from '@reconcept-platform/shared/data-access';
import {
  type AttachedFileFragment,
  type CommentAttachmentsQueryQuery,
  type CommentAttachmentsQueryQueryVariables,
  type CreateCardCommentInput,
  type CreateCardCommentMutation,
  type CreateCardCommentMutationVariables,
  type DestroyCardCommentMutation,
  type DestroyCardCommentMutationVariables,
  type UpdateCardCommentInput,
  type UpdateCardCommentMutation,
  type UpdateCardCommentMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { getLogRefetchQueries } from '../../utils/log-query-refetch';
import { isGoalCardId } from '../../utils/typename.utils';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { eventCardWithCommentsQuery } from '../event-card/event-card.queries';
import { goalCardWithCommentsQuery } from '../goal-card/goal-card.queries';
import { commentDetailFragment } from './comment.fragments';
import { CommentAttachmentsQuery } from './comment.queries';

@Injectable({
  providedIn: 'root',
})
export class CommentApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchCardCommentAttachedFiles(commentId: string): Observable<AttachedFileFragment[]> {
    if (!commentId) {
      return of([]);
    }

    return this.workspaceGraphQLService
      .watchQuery<CommentAttachmentsQueryQuery, CommentAttachmentsQueryQueryVariables>({
        query: CommentAttachmentsQuery,
        variables: {
          id: commentId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'CardComment').attachedFiles || []));
  }

  createCardComment(createCardCommentInput: CreateCardCommentInput) {
    const logRefetchQueries = getLogRefetchQueries(createCardCommentInput.cardId);

    const cardWithCommentsRefetchQueries = isGoalCardId(createCardCommentInput.cardId)
      ? goalCardWithCommentsQuery
      : eventCardWithCommentsQuery;

    return (
      this.workspaceGraphQLService
        .mutateV2<CreateCardCommentMutation, CreateCardCommentMutationVariables>({
          mutation: gql`
            mutation CreateCardComment($input: CreateCardCommentInput!) {
              createCardComment(input: $input) {
                cardComment {
                  ...CommentDetailFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${commentDetailFragment}
          `,
          variables: {
            input: createCardCommentInput,
          },
          refetchQueries: [
            ...logRefetchQueries,
            {
              query: cardWithCommentsRefetchQueries,
              variables: {
                id: createCardCommentInput.cardId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createCardComment.cardComment))
    );
  }

  destroyCardComment(cardId: string, cardCommentId: string) {
    const logRefetchQueries = getLogRefetchQueries(cardId);

    return (
      this.workspaceGraphQLService
        .mutateV2<DestroyCardCommentMutation, DestroyCardCommentMutationVariables>({
          mutation: gql`
            mutation DestroyCardComment($input: DestroyCardCommentInput!) {
              destroyCardComment(input: $input) {
                cardComment {
                  ...CommentDetailFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${commentDetailFragment}
          `,
          variables: {
            input: {
              cardCommentId,
            },
          },
          refetchQueries: logRefetchQueries,
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.destroyCardComment.cardComment))
    );
  }

  updateCardComment(updateCardTaskInput: UpdateCardCommentInput, cardId: string) {
    const logRefetchQueries = getLogRefetchQueries(cardId);

    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateCardCommentMutation, UpdateCardCommentMutationVariables>({
          mutation: gql`
            mutation UpdateCardComment($input: UpdateCardCommentInput!) {
              updateCardComment(input: $input) {
                cardComment {
                  ...CommentDetailFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${commentDetailFragment}
          `,
          variables: {
            input: updateCardTaskInput,
          },
          refetchQueries: logRefetchQueries,
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateCardComment.cardComment))
    );
  }
}
